/**
 * Плагин валидации для CSElement
 * Обеспечивает проверку данных, схем и ограничений с полной поддержкой JSON Schema
 */

import Ajv, { ValidateFunction, ErrorObject } from 'ajv';
import addFormats from 'ajv-formats';
import { JSONSchema7 } from 'json-schema';
import { CSElementPlugin, LifecycleHooks, PluginOptions } from '../types/plugin-interfaces';
import { ICSElement } from '../types/interfaces';
import { CSElement } from '../core/CSElement';

/**
 * Типы валидации
 */
export enum ValidationType {
  REQUIRED = 'required',
  TYPE = 'type',
  RANGE = 'range',
  PATTERN = 'pattern',
  CUSTOM = 'custom',
  SCHEMA = 'schema',
  JSON_SCHEMA = 'json_schema'
}

/**
 * Правило валидации для плагина
 */
export interface ValidationRule {
  type: ValidationType;
  field: string;
  message?: string;
  expectedType?: string;
  min?: number;
  max?: number;
  pattern?: string;
  validator?: (value: any) => boolean | string;
  jsonSchema?: JSONSchema7;
}

/**
 * Результат валидации плагина
 */
export interface ValidationResult {
  valid: boolean;
  field: string;
  message: string;
  value?: any;
  errors?: ErrorObject[];
  schemaPath?: string;
}

/**
 * Схема валидации (legacy, для обратной совместимости)
 */
export interface ValidationSchema {
  type: string;
  properties?: Record<string, any>;
  required?: string[];
  [key: string]: any;
}

/**
 * Расширенная схема валидации на основе JSON Schema
 */
export interface JSONSchemaValidation {
  $id: string;
  schema: JSONSchema7;
  compiled?: ValidateFunction;
  strict?: boolean;
  removeAdditional?: boolean | 'all' | 'failing';
}

/**
 * Конфигурация валидации
 */
export interface ValidationConfig {
  enabled: boolean;
  strict: boolean; // Блокировать операции при ошибках валидации
  autoValidate: boolean; // Автоматическая валидация при изменениях
  rules: ValidationRule[];
  schemas: { [key: string]: ValidationSchema }; // Legacy schemas
  jsonSchemas: { [key: string]: JSONSchemaValidation }; // JSON Schema 7 schemas
  ajvOptions?: {
    allErrors?: boolean;
    verbose?: boolean;
    strict?: boolean;
    removeAdditional?: boolean | 'all' | 'failing';
    useDefaults?: boolean;
    coerceTypes?: boolean;
    addUsedSchema?: boolean;
  };
}

/**
 * Результат компиляции схемы
 */
export interface SchemaCompilationResult {
  success: boolean;
  schemaId: string;
  errors?: string[];
}

/**
 * Плагин валидации с поддержкой JSON Schema
 */
export class ValidationPlugin implements CSElementPlugin {
  public readonly name = 'ValidationPlugin';
  public readonly version = '2.0.0';
  public readonly description = 'Плагин для валидации данных элементов с поддержкой JSON Schema';

  private config: ValidationConfig = {
    enabled: true,
    strict: false,
    autoValidate: true,
    rules: [],
    schemas: {},
    jsonSchemas: {},
    ajvOptions: {
      allErrors: true,
      verbose: true,
      strict: false,
      removeAdditional: false,
      useDefaults: true,
      coerceTypes: false,
      addUsedSchema: false
    }
  };

  private validationResults = new Map<string, ValidationResult[]>();
  private ajv: Ajv;

  constructor(options?: PluginOptions) {
    if (options?.config) {
      this.config = { ...this.config, ...options.config };
    }

    // Инициализация Ajv с настройками
    this.ajv = new Ajv(this.config.ajvOptions);
    addFormats(this.ajv);

    // Компилируем существующие JSON Schema
    this.compileAllSchemas();
  }

  install(CSElementClass: typeof CSElement): void {
    console.log(`ValidationPlugin v${this.version} установлен для ${CSElementClass.name}`);
  }

  getLifecycleHooks(): LifecycleHooks {
    return {
      beforeCreate: async (_name: string) => {
        // Валидация при создании элемента
      },

      afterCreate: async (element: ICSElement) => {
        if (this.config.enabled) {
          await this.validateElement(element as CSElement);
        }
      },

      beforeSetData: async (element: ICSElement, key: string, value: any) => {
        if (this.config.enabled && this.config.autoValidate) {
          const results = await this.validateData(element, key, value);
          if (this.config.strict && results.some(r => !r.valid)) {
            throw new Error(`Валидация данных не пройдена: ${results.filter(r => !r.valid).map(r => r.message).join(', ')}`);
          }
        }
      }
    };
  }

  /**
   * Валидация элемента
   */
  async validateElement(element: CSElement): Promise<ValidationResult[]> {
    const results: ValidationResult[] = [];

    // Валидация по правилам
    for (const rule of this.config.rules) {
      const value = element.getData(rule.field);
      const result = this.validateField(rule, value);
      if (result) {
        results.push(result);
      }
    }

    // Валидация по legacy схемам
    for (const [, schema] of Object.entries(this.config.schemas)) {
      const schemaResults = this.validateBySchema(element, schema);
      results.push(...schemaResults);
    }

    // Валидация по JSON Schema
    for (const [schemaId, jsonSchema] of Object.entries(this.config.jsonSchemas)) {
      const jsonSchemaResults = await this.validateByJSONSchema(element, schemaId, jsonSchema);
      results.push(...jsonSchemaResults);
    }

    this.validationResults.set(element.id, results);
    return results;
  }

  /**
   * Валидация конкретного поля данных
   */
  async validateData(_element: ICSElement, key: string, value: any): Promise<ValidationResult[]> {
    const results: ValidationResult[] = [];

    // Применяем правила для конкретного поля
    const fieldRules = this.config.rules.filter(rule => rule.field === key || !rule.field);
    
    for (const rule of fieldRules) {
      const result = this.validateField(rule, value);
      if (result) {
        results.push(result);
      }
    }

    // Валидация поля по JSON Schema
    for (const [schemaId, jsonSchema] of Object.entries(this.config.jsonSchemas)) {
      const fieldResults = await this.validateFieldByJSONSchema(key, value, schemaId, jsonSchema);
      results.push(...fieldResults);
    }

    return results;
  }

  /**
   * Валидация всего объекта данных элемента по JSON Schema
   */
  async validateByJSONSchema(element: CSElement, schemaId: string, jsonSchema: JSONSchemaValidation): Promise<ValidationResult[]> {
    const results: ValidationResult[] = [];

    if (!jsonSchema.compiled) {
      results.push({
        valid: false,
        field: '_schema',
        message: `JSON Schema ${schemaId} не скомпилирована`,
        schemaPath: schemaId
      });
      return results;
    }

    // Получаем все данные элемента
    const elementData = this.extractElementData(element);

    try {
      const isValid = jsonSchema.compiled(elementData);
      
      if (!isValid && jsonSchema.compiled.errors) {
        for (const error of jsonSchema.compiled.errors) {
          results.push({
            valid: false,
            field: error.instancePath || error.schemaPath || '_root',
            message: this.formatJSONSchemaError(error),
            value: error.data,
            errors: [error],
            schemaPath: error.schemaPath
          });
        }
      }
    } catch (error) {
      results.push({
        valid: false,
        field: '_schema',
        message: `Ошибка валидации JSON Schema: ${error instanceof Error ? error.message : String(error)}`,
        schemaPath: schemaId
      });
    }

    return results;
  }

  /**
   * Валидация отдельного поля по JSON Schema
   */
  async validateFieldByJSONSchema(field: string, value: any, schemaId: string, jsonSchema: JSONSchemaValidation): Promise<ValidationResult[]> {
    const results: ValidationResult[] = [];

    if (!jsonSchema.compiled || !jsonSchema.schema.properties) {
      return results;
    }

    // Проверяем есть ли схема для этого поля
    const fieldSchema = jsonSchema.schema.properties[field];
    if (!fieldSchema) {
      return results;
    }

    try {
      // Создаем временную схему только для этого поля
      const tempSchema: JSONSchema7 = {
        type: 'object',
        properties: {
          [field]: fieldSchema
        }
      };

      const tempValidator = this.ajv.compile(tempSchema);
      const testData = { [field]: value };
      
      const isValid = tempValidator(testData);
      
      if (!isValid && tempValidator.errors) {
        for (const error of tempValidator.errors) {
          results.push({
            valid: false,
            field: field,
            message: this.formatJSONSchemaError(error),
            value: value,
            errors: [error],
            schemaPath: error.schemaPath
          });
        }
      }
    } catch (error) {
      results.push({
        valid: false,
        field: field,
        message: `Ошибка валидации поля по JSON Schema: ${error instanceof Error ? error.message : String(error)}`,
        schemaPath: schemaId
      });
    }

    return results;
  }

  private validateField(rule: ValidationRule, value: any): ValidationResult | null {
    let isValid = true;
    let message = rule.message || `Ошибка валидации поля ${rule.field}`;

    switch (rule.type) {
      case ValidationType.REQUIRED:
        isValid = value !== undefined && value !== null && value !== '';
        if (!isValid) {
          message = rule.message || `Поле ${rule.field} обязательно`;
        }
        break;

      case ValidationType.TYPE:
        if (value !== undefined && value !== null) {
          const actualType = typeof value;
          isValid = actualType === rule.expectedType;
          if (!isValid) {
            message = rule.message || `${rule.field} должен быть типа ${rule.expectedType}`;
          }
        } else if (rule.expectedType) {
          isValid = false;
          message = rule.message || `${rule.field} должен быть типа ${rule.expectedType}`;
        }
        break;

      case ValidationType.RANGE:
        if (value !== undefined && value !== null) {
          if (typeof value === 'number') {
            isValid = true;
            if (rule.min !== undefined && value < rule.min) {
              isValid = false;
              message = rule.message || `Значение должно быть не менее ${rule.min}`;
            }
            if (rule.max !== undefined && value > rule.max) {
              isValid = false;
              message = rule.message || `Значение должно быть не более ${rule.max}`;
            }
          } else {
            isValid = false;
            message = rule.message || `Значение должно быть числом для проверки диапазона`;
          }
        } else {
          isValid = false;
          message = rule.message || `Значение должно быть числом для проверки диапазона`;
        }
        break;

      case ValidationType.PATTERN:
        if (value !== undefined && value !== null && rule.pattern) {
          if (typeof value === 'string') {
            const regex = new RegExp(rule.pattern);
            isValid = regex.test(value);
            if (!isValid) {
              message = rule.message || `Значение не соответствует шаблону`;
            }
          } else {
            isValid = false;
            message = rule.message || `Значение должно быть строкой для проверки по шаблону`;
          }
        } else if (value === undefined || value === null) {
          isValid = false;
          message = rule.message || `Значение должно быть строкой для проверки по шаблону`;
        }
        break;

      case ValidationType.CUSTOM:
        if (rule.validator) {
          if (value !== undefined && value !== null) {
            try {
              const validationResult = rule.validator(value);
              if (typeof validationResult === 'boolean') {
                isValid = validationResult;
                if (!isValid) {
                  message = rule.message || `Пользовательская валидация не пройдена`;
                }
              } else {
                isValid = false;
                message = typeof validationResult === 'string' ? validationResult : rule.message || `Пользовательская валидация не пройдена`;
              }
            } catch (error) {
              isValid = false;
              message = `Ошибка валидации: ${error instanceof Error ? error.message : String(error)}`;
            }
          } else {
            isValid = false;
            message = rule.message || `Пользовательская валидация не пройдена`;
          }
        }
        break;

      case ValidationType.JSON_SCHEMA:
        if (rule.jsonSchema) {
          try {
            const validator = this.ajv.compile(rule.jsonSchema);
            isValid = validator(value);
            if (!isValid && validator.errors) {
              message = rule.message || this.formatJSONSchemaErrors(validator.errors);
            }
          } catch (error) {
            isValid = false;
            message = `Ошибка компиляции JSON Schema: ${error instanceof Error ? error.message : String(error)}`;
          }
        }
        break;
    }

    if (!isValid) {
      return {
        valid: false,
        field: rule.field,
        message,
        value
      };
    }

    return null;
  }

  /**
   * Валидация по схеме (legacy)
   */
  private validateBySchema(element: CSElement, schema: ValidationSchema): ValidationResult[] {
    const results: ValidationResult[] = [];

    if (schema.required) {
      for (const field of schema.required) {
        const value = element.getData(field);
        if (value === undefined || value === null || value === '') {
          results.push({
            valid: false,
            field,
            message: `Поле ${field} обязательно`,
            value
          });
        }
      }
    }

    if (schema.properties) {
      for (const [field, fieldSchema] of Object.entries(schema.properties)) {
        const value = element.getData(field);
        if (value !== undefined && value !== null) {
          const fieldResults = this.validateFieldBySchema(field, value, fieldSchema);
          results.push(...fieldResults);
        }
      }
    }

    return results;
  }

  /**
   * Валидация поля по схеме (legacy)
   */
  private validateFieldBySchema(field: string, value: any, fieldSchema: any): ValidationResult[] {
    const results: ValidationResult[] = [];

    // Проверка типа
    if (fieldSchema.type) {
      const actualType = typeof value;
      const expectedType = fieldSchema.type;
      
      if (expectedType === 'integer') {
        if (!Number.isInteger(value)) {
          results.push({
            valid: false,
            field,
            message: `${field} должен быть целым числом`,
            value
          });
        }
      } else if (actualType !== expectedType) {
        results.push({
          valid: false,
          field,
          message: `${field} должен быть типа ${expectedType}`,
          value
        });
      }
    }

    // Проверка минимальной длины для строк
    if (fieldSchema.minLength && typeof value === 'string') {
      if (value.length < fieldSchema.minLength) {
        results.push({
          valid: false,
          field,
          message: `${field} должен быть не менее ${fieldSchema.minLength} символов`,
          value
        });
      }
    }

    // Проверка максимальной длины для строк
    if (fieldSchema.maxLength && typeof value === 'string') {
      if (value.length > fieldSchema.maxLength) {
        results.push({
          valid: false,
          field,
          message: `${field} должен быть не более ${fieldSchema.maxLength} символов`,
          value
        });
      }
    }

    // Проверка минимального значения для чисел
    if (fieldSchema.minimum !== undefined && typeof value === 'number') {
      if (value < fieldSchema.minimum) {
        results.push({
          valid: false,
          field,
          message: `${field} должен быть не менее ${fieldSchema.minimum}`,
          value
        });
      }
    }

    // Проверка максимального значения для чисел
    if (fieldSchema.maximum !== undefined && typeof value === 'number') {
      if (value > fieldSchema.maximum) {
        results.push({
          valid: false,
          field,
          message: `${field} должен быть не более ${fieldSchema.maximum}`,
          value
        });
      }
    }

    return results;
  }

  /**
   * Извлечение данных элемента для валидации
   */
  private extractElementData(element: CSElement): Record<string, any> {
    // Используем публичный метод getDataAsObject() для получения всех данных
    return element.getDataAsObject();
  }

  /**
   * Форматирование ошибки JSON Schema
   */
  private formatJSONSchemaError(error: ErrorObject): string {
    const field = error.instancePath || error.schemaPath || 'unknown';
    const keyword = error.keyword;
    const message = error.message || 'Validation failed';
    
    switch (keyword) {
      case 'required':
        return `Обязательное поле отсутствует: ${error.params?.missingProperty || field}`;
      case 'type':
        return `Неверный тип данных в поле ${field}: ожидается ${error.params?.type}, получен ${typeof error.data}`;
      case 'minimum':
        return `Значение в поле ${field} должно быть не менее ${error.params?.limit}`;
      case 'maximum':
        return `Значение в поле ${field} должно быть не более ${error.params?.limit}`;
      case 'minLength':
        return `Длина значения в поле ${field} должна быть не менее ${error.params?.limit} символов`;
      case 'maxLength':
        return `Длина значения в поле ${field} должна быть не более ${error.params?.limit} символов`;
      case 'pattern':
        return `Значение в поле ${field} не соответствует шаблону ${error.params?.pattern}`;
      case 'format':
        return `Значение в поле ${field} не соответствует формату ${error.params?.format}`;
      case 'enum':
        return `Значение в поле ${field} должно быть одним из: ${error.params?.allowedValues?.join(', ')}`;
      default:
        return `Ошибка валидации в поле ${field}: ${message}`;
    }
  }

  /**
   * Форматирование множественных ошибок JSON Schema
   */
  private formatJSONSchemaErrors(errors: ErrorObject[]): string {
    return errors.map(error => this.formatJSONSchemaError(error)).join('; ');
  }

  /**
   * Компиляция всех JSON Schema
   */
  private compileAllSchemas(): SchemaCompilationResult[] {
    const results: SchemaCompilationResult[] = [];

    for (const [schemaId, jsonSchema] of Object.entries(this.config.jsonSchemas)) {
      const result = this.compileSchema(schemaId, jsonSchema);
      results.push(result);
    }

    return results;
  }

  /**
   * Компиляция одной JSON Schema
   */
  compileSchema(schemaId: string, jsonSchema: JSONSchemaValidation): SchemaCompilationResult {
    try {
      jsonSchema.compiled = this.ajv.compile(jsonSchema.schema);
      return {
        success: true,
        schemaId
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return {
        success: false,
        schemaId,
        errors: [errorMessage]
      };
    }
  }

  /**
   * Получение результатов валидации для элемента
   */
  getValidationResults(elementId: string): ValidationResult[] {
    return this.validationResults.get(elementId) || [];
  }

  /**
   * Добавление правила валидации
   */
  addRule(rule: ValidationRule): void {
    this.config.rules.push(rule);
  }

  /**
   * Добавление схемы валидации (legacy)
   */
  addSchema(name: string, schema: ValidationSchema): void {
    this.config.schemas[name] = schema;
  }

  /**
   * Добавление JSON Schema
   */
  addJSONSchema(schemaId: string, schema: JSONSchema7, options?: {
    strict?: boolean;
    removeAdditional?: boolean | 'all' | 'failing';
  }): SchemaCompilationResult {
    const jsonSchema: JSONSchemaValidation = {
      $id: schemaId,
      schema,
      strict: options?.strict,
      removeAdditional: options?.removeAdditional
    };

    this.config.jsonSchemas[schemaId] = jsonSchema;
    return this.compileSchema(schemaId, jsonSchema);
  }

  /**
   * Удаление JSON Schema
   */
  removeJSONSchema(schemaId: string): boolean {
    if (this.config.jsonSchemas[schemaId]) {
      delete this.config.jsonSchemas[schemaId];
      // Удаляем из Ajv если была скомпилирована
      try {
        this.ajv.removeSchema(schemaId);
      } catch {
        // Игнорируем ошибки удаления
      }
      return true;
    }
    return false;
  }

  /**
   * Получение JSON Schema по ID
   */
  getJSONSchema(schemaId: string): JSONSchemaValidation | undefined {
    return this.config.jsonSchemas[schemaId];
  }

  /**
   * Получение всех JSON Schema
   */
  getAllJSONSchemas(): { [key: string]: JSONSchemaValidation } {
    return { ...this.config.jsonSchemas };
  }

  /**
   * Валидация произвольных данных по JSON Schema
   */
  validateDataByJSONSchema(data: any, schemaId: string): ValidationResult[] {
    const results: ValidationResult[] = [];
    const jsonSchema = this.config.jsonSchemas[schemaId];

    if (!jsonSchema) {
      results.push({
        valid: false,
        field: '_schema',
        message: `JSON Schema с ID ${schemaId} не найдена`
      });
      return results;
    }

    if (!jsonSchema.compiled) {
      const compileResult = this.compileSchema(schemaId, jsonSchema);
      if (!compileResult.success) {
        results.push({
          valid: false,
          field: '_schema',
          message: `Не удалось скомпилировать JSON Schema: ${compileResult.errors?.join(', ')}`
        });
        return results;
      }
    }

    try {
      const isValid = jsonSchema.compiled!(data);
      
      if (!isValid && jsonSchema.compiled!.errors) {
        for (const error of jsonSchema.compiled!.errors) {
          results.push({
            valid: false,
            field: error.instancePath || error.schemaPath || '_root',
            message: this.formatJSONSchemaError(error),
            value: error.data,
            errors: [error],
            schemaPath: error.schemaPath
          });
        }
      }
    } catch (error) {
      results.push({
        valid: false,
        field: '_schema',
        message: `Ошибка валидации: ${error instanceof Error ? error.message : String(error)}`
      });
    }

    return results;
  }

  /**
   * Очистка результатов валидации
   */
  clearValidationResults(elementId?: string): void {
    if (elementId) {
      this.validationResults.delete(elementId);
    } else {
      this.validationResults.clear();
    }
  }

  /**
   * Обновление конфигурации
   */
  updateConfig(config: Partial<ValidationConfig>): void {
    this.config = { ...this.config, ...config };
    
    // Пересоздаем Ajv если изменились его настройки
    if (config.ajvOptions) {
      this.ajv = new Ajv(this.config.ajvOptions);
      addFormats(this.ajv);
      this.compileAllSchemas();
    }
  }

  /**
   * Получение конфигурации
   */
  getConfig(): ValidationConfig {
    return { ...this.config };
  }

  /**
   * Получение всех правил
   */
  getRules(): ValidationRule[] {
    return [...this.config.rules];
  }

  /**
   * Получение экземпляра Ajv для расширенного использования
   */
  getAjvInstance(): Ajv {
    return this.ajv;
  }

  /**
   * Проверка поддержки JSON Schema Draft версии
   */
  getSupportedJSONSchemaDraft(): string {
    return 'draft-07';
  }

  /**
   * Получение статистики валидации
   */
  getValidationStats(): {
    totalElements: number;
    elementsWithErrors: number;
    totalErrors: number;
    totalJSONSchemas: number;
    totalRules: number;
  } {
    let totalErrors = 0;
    let elementsWithErrors = 0;

    for (const results of this.validationResults.values()) {
      const errors = results.filter(r => !r.valid);
      if (errors.length > 0) {
        elementsWithErrors++;
        totalErrors += errors.length;
      }
    }

    return {
      totalElements: this.validationResults.size,
      elementsWithErrors,
      totalErrors,
      totalJSONSchemas: Object.keys(this.config.jsonSchemas).length,
      totalRules: this.config.rules.length
    };
  }
}

/**
 * Фабричная функция для создания плагина валидации
 */
export function validationPlugin(config?: Partial<ValidationConfig>): ValidationPlugin {
  return new ValidationPlugin({ config });
}

/**
 * Фабричная функция для создания плагина валидации с JSON Schema
 */
export function jsonSchemaValidationPlugin(
  schemas: { [schemaId: string]: JSONSchema7 },
  config?: Partial<ValidationConfig>
): ValidationPlugin {
  const plugin = new ValidationPlugin({ config });
  
  // Добавляем все переданные схемы
  for (const [schemaId, schema] of Object.entries(schemas)) {
    plugin.addJSONSchema(schemaId, schema);
  }
  
  return plugin;
} 